/**
 * The narrowing INTERCEPTORS (D41 / D56 / D63) — readings that claim a command
 * before the ordinary verb roster sees it.
 *
 * @remarks
 * Three special subjects here (a fourth, the actor, in `narrow-actor-aimed.ts`
 * since D232), one shape: each looks at the proposal, decides
 * whether it is THEIRS, and returns `undefined` — "not mine, carry on" —
 * otherwise. The room (D41) narrows on its own terms because carrying a room
 * is not a thing anyone can do; the hands (D63) exist so the engine's own
 * "Open it with what?" accepts the answer a person gives; and the per-item
 * switch (D56) is how `turn` stays declared-unsupported for everything that
 * is not a switch.
 *
 * Split from `narrow-parsed-command.ts` when D63's interceptor pushed it past
 * the 500-line cap — and because these are a different KIND of rule from the
 * roster: the roster maps verbs; these claim subjects. The import cycle with
 * the roster module is deliberate and safe: each side only CALLS the other's
 * functions at narrowing time, long after both modules initialize.
 *
 * @packageDocumentation
 */

import type { ItemId, RoomId, RuntimeState } from '@orpheus/content-schema';
import type { EntityRef, ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import { actorIdForEntityRef, BODY_NAME, isBodyRef, itemIdForEntityRef, roomIdForEntityRef, roomNameInPlayersWord } from '../entities/entity-ref.js';
import { isSwitchedOn } from '../entities/item-state.js';
import { applicableDoorRefusal, canOpenByHand, narrowed, rejected } from './narrow-parsed-command.js';

/**
 * The item this command is aimed at, wherever the grammar put it.
 *
 * @remarks
 * D56 extracted this because "the thing the player aimed at" had been spelled
 * three different ways across four sites in this file, and the shortest
 * spelling was a live hole: `narrowSwitchCommand` omitted `toolRef`, so a pack
 * declaring a `with` binding on `turn` would have had the switch decline the
 * very item `refuseUnsupportedIntent` — which DOES read `toolRef` — then
 * refused by name.
 *
 * Direct object first, then the bound phrase, then the tool slot: most specific
 * binding wins, and the fallbacks only matter for packs whose vocabulary binds
 * prepositions on the verb in question.
 *
 * @param command - The parsed proposal
 * @returns The aimed item's id, or `undefined` when the clause named none
 */
const aimedItemId = (command: ParsedCommand): ItemId | undefined => {
	const objectRef = command.directObjectRef ?? command.indirectObjectRef ?? command.toolRef;

	return objectRef === undefined ? undefined : itemIdForEntityRef(objectRef);
};

/**
 * Narrow a switch command — `turn X on` / `turn X off` (D56), and (D268) a
 * bare `switch X`, `flip X` or `toggle X`.
 *
 * @remarks
 * Returns `undefined` — meaning "not mine, carry on" — for everything that is
 * not a `turn` aimed at a thing that declares `switchable`. That is several
 * separate ways to decline, and each one lands somewhere better than a switch
 * would:
 *
 * - a different verb falls through to the ordinary switch below;
 * - `turn X` with NO particle names no direction, and D56 refused it rather
 *   than guess. D268 (Mike, on the Meridian's breakers: `flip breaker 2`,
 *   `switch breaker 2`) found the guess was never one: a switch has two
 *   states, so a bare switch asks for the one it is not in, and the live
 *   state says which. Only without a state (tooling) is there nothing to read,
 *   and there it keeps its D47 refusal;
 * - `turn on` naming no item at all keeps it too (unreachable through the
 *   shipped vocabulary, where `turn` requires an object, but reachable for a
 *   pack that declares it optional);
 * - `turn on X` where X has no switch keeps its D47 refusal, which is how
 *   Calder's lamp and console answer in character instead of toggling.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param intentText - The canonical verb text
 * @returns The narrowed switch action, or `undefined` when this is not one
 */
export const narrowSwitchCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome | undefined => {
	const targetItemId = intentText === 'turn' ? aimedItemId(command) : undefined;
	const target = targetItemId === undefined ? undefined : requireItem(bundle, targetItemId);

	if (target === undefined || target.switchable === undefined) {
		return undefined;
	}

	const way = command.particle === undefined ? otherWayOf(target, state) : SWITCH_WAYS.find((candidate) => candidate === command.particle);

	return way === undefined ? undefined : narrowed({ itemId: target.itemId, type: way === 'on' ? 'switch_on' : 'switch_off' });
};

/** The two particles a switch answers to. */
const SWITCH_WAYS = ['on', 'off'] as const;

/**
 * D268: the way a bare switch goes — the way the switch is not, read off the live state; nothing without one.
 *
 * @param target - The switch
 * @param state - The live runtime state, when the caller has one
 * @returns `on` or `off`, or `undefined` when there is no state to read
 */
const otherWayOf = (target: Parameters<typeof isSwitchedOn>[0], state: RuntimeState | undefined): (typeof SWITCH_WAYS)[number] | undefined => {
	if (state === undefined) {
		return undefined;
	}

	return isSwitchedOn(target, state) ? 'off' : 'on';
};

/**
 * Narrow a command that involves the player's HANDS (D63).
 *
 * @remarks
 * Two shapes, in priority order:
 *
 * - **Hands as the INSTRUMENT** (`open the locker with my hands`, `use hands
 *   on the grille`): exactly the bare-hands attempt, so a hand-openable door
 *   opens, and anything else earns `handsAreNotEnough` — an honest refusal to
 *   the engine's own question, instead of "You see no hands here" (D213 words).
 *   D184 (Calder's finding on the deployed demo): a thing that has said in
 *   its OWN words that it does not open (`openRefusal`, its `when` holding)
 *   says that first — `handsAreNotEnough` promises a tool exists, and a
 *   door with no door has no tool problem.
 * - **Hands as the OBJECT**: `inspect` gets its one sentence; every other verb
 *   gets the number-agnostic refusal frame with `your hands` as the subject —
 *   the same slot any unmechanized verb-on-thing earns, which is exactly what
 *   aiming TAKE at your own hands is.
 *
 * Neither shape may fall through: the body ref is not an item id, and every
 * later arm assumes item refs resolve through `requireItem`.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param intentText - The canonical verb text
 * @param state - The live runtime state, when the caller has one
 * @returns The narrowed outcome, or `undefined` when no hands are involved
 */
export const narrowBodyAimedCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome | undefined => {
	const instrumentRef = command.toolRef ?? command.indirectObjectRef;
	const isHandsInstrument = instrumentRef !== undefined && isBodyRef(instrumentRef);

	if (isHandsInstrument && (intentText === 'open' || intentText === 'use')) {
		const targetRef = command.directObjectRef;

		if (targetRef === undefined || isBodyRef(targetRef)) {
			return rejected(bundle, state, { detail: `${intentText} with hands arrived without a target`, type: 'malformed_command' }, [
				speakSlot(bundle, state, 'malformedCommand'),
			]);
		}

		const targetItemId = itemIdForEntityRef(targetRef);
		const target = requireItem(bundle, targetItemId);
		const openRefusal = applicableDoorRefusal(target.openRefusal, bundle, state);

		if (openRefusal !== undefined) {
			return rejected(bundle, state, { itemId: targetItemId, type: 'open_refused' }, openRefusal.narratives);
		}

		if (canOpenByHand(target, bundle, state)) {
			return narrowed({ itemId: targetItemId, type: 'open_container' });
		}

		return rejected(bundle, state, { itemId: targetItemId, type: 'hands_are_not_enough' }, [
			speakSlot(bundle, state, 'handsAreNotEnough', { itemName: target.name }),
		]);
	}

	const objectRef = command.directObjectRef ?? command.indirectObjectRef;

	if (objectRef === undefined || !isBodyRef(objectRef)) {
		return undefined;
	}

	if (intentText === 'inspect' || intentText === 'look') {
		return rejected(bundle, state, { type: 'body_aside' }, [speakSlot(bundle, state, 'handsDescription')]);
	}

	return rejected(bundle, state, { type: 'body_aside' }, [
		speakSlot(bundle, state, 'unsupportedIntentOnTarget', {
			intent: command.intentSurface.toUpperCase(),
			itemName: BODY_NAME,
			verb: command.intentSurface.toLowerCase(),
		}),
	]);
};

/**
 * The reference a sentence is ABOUT — the slot the room intercept reads.
 *
 * @remarks
 * For every verb but `ask`, the direct object, then the bound phrase, then
 * the tool slot: the same precedence {@link aimedItemId} and the D35 refusal
 * read by, so a room named in ANY slot of a sentence that names nothing else
 * is the room intercept's (D225: `kick with the room` once reached
 * `requireItem` through the tool slot alone). `ask` is about its TOPIC — `ask
 * the man about the lane` is a question about the lane — and the roster's
 * `ask` arm reads the bound phrase first for exactly that reason; reading the
 * direct object first here let the man hide the lane, and a room reference
 * reached `handleAsk` as an item id (D225's E1).
 *
 * @param command - The parsed proposal
 * @param intentText - The canonical verb text
 * @returns The reference the sentence is about, or `undefined` when it names nothing
 */
const aboutRef = (command: ParsedCommand, intentText: string): EntityRef | undefined =>
	intentText === 'ask'
		? (command.indirectObjectRef ?? command.directObjectRef)
		: (command.directObjectRef ?? command.indirectObjectRef ?? command.toolRef);

/**
 * Refuse a verb aimed at the room itself, in the one sentence the engine has
 * for it (D41, D44): the room is named where only a thing could act.
 *
 * @remarks
 * Shared by the intercept's fall-through and by the instrument paths (D225):
 * `use the chain on the ruts`, `open the door with the ruts` and `use the
 * ruts` are three spellings of one impossibility — a room is not a thing a
 * tool meets or that meets a tool — and they say the same sentence, so the
 * refusal cannot drift between the slot a room was named in.
 *
 * D226 (E20, Calder's `what-a-command-was-about` §2): the sentence names
 * the room by the PLAYER'S word when that word was the room's own — "Nothing
 * about the ruts answers to USE." — the way an item's refusal names the
 * item. The player said "ruts"; "the room itself" was the engine explaining
 * its model instead of answering. The engine's own room words (`here`,
 * `room`, `place`) keep the framing, which is the only sentence they make.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle (response table)
 * @param intentText - The canonical verb text
 * @param state - The live runtime state, when the caller has one
 * @returns The typed refusal
 */
export const refuseRoomAimedVerb = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome =>
	rejected(bundle, state, { intentToken: intentText, type: 'unsupported_intent' }, [
		speakSlot(bundle, state, 'unsupportedIntentOnTarget', {
			intent: command.intentSurface.toUpperCase(),
			itemName: roomNameInPlayersWord(roomSurfaceOf(command)),
			verb: command.intentSurface.toLowerCase(),
		}),
	]);

/**
 * The words the player used for the room, in whichever slot they named it
 * (D226, E20) — the surface beside the first room reference the command
 * carries, in the precedence the intercept reads.
 *
 * @param command - The parsed proposal
 * @returns The player's head words for the room, or `undefined` when the parser kept none
 */
const roomSurfaceOf = (command: ParsedCommand): string | undefined => {
	const slots: readonly { readonly ref: EntityRef | undefined; readonly surface: string | undefined }[] = [
		{ ref: command.directObjectRef, surface: command.directObjectSurface },
		{ ref: command.indirectObjectRef, surface: command.indirectObjectSurface },
		{ ref: command.toolRef, surface: command.toolSurface },
	];

	return slots.find((slot) => slot.ref !== undefined && roomIdForEntityRef(slot.ref) !== undefined)?.surface;
};

/**
 * Narrow a command whose object is the ROOM ITSELF (D41).
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle (response table)
 * @param intentText - The canonical verb text
 * @returns The narrowed outcome, or `undefined` when no room was named
 */
export const narrowRoomAimedCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome | undefined => {
	const objectRef = aboutRef(command, intentText);
	const roomId = objectRef === undefined ? undefined : roomIdForEntityRef(objectRef);

	if (roomId === undefined) {
		return undefined;
	}

	// Examining or reading the room around you is exactly LOOK.
	if (intentText === 'inspect' || intentText === 'look') {
		return narrowed({ type: 'look' });
	}

	// D137: a room that authors `askNarratives` is askable; one that does not
	// keeps the D63 sentence below, which is true of it. D231: a room with
	// `topics` is askable too, and so is a room asked about THROUGH a thing
	// that has topics (`ask the voice about the room` — her table may hold
	// the room). An askable room falls through to the roster's `ask` arm,
	// which reads the room as one party of the question.
	if (intentText === 'ask' && isRoomAskable(command, bundle, roomId)) {
		return undefined;
	}

	// D62 overturned the D41-era reading that searching where you stand is
	// looking: `search the room` is the room-level SURVEY now — a real act with
	// its own answer, ruled by Mike after Calder's sweep showed the alias just
	// repeating the description a player had already read.
	if (intentText === 'search') {
		return narrowed({ type: 'search_room' });
	}

	// D227: a verb that stands for a direction (`climb` → up), aimed at the
	// place the player is standing in by any of its words — `climb the road`,
	// `climb the switchback` — is that direction: the place is where you
	// already are, so climbing IT can only mean climbing FROM it, which is
	// what the bare verb does. Aimed at a thing (`climb the mountain`, an
	// ambient item), the thing's own answer stands, as it always has.
	const bareDirection = bundle.compiledLexicon.intentsByToken.get(intentText)?.bareDirection;

	if (bareDirection !== undefined) {
		return narrowed({ directionToken: bareDirection, type: 'go' });
	}

	return refuseRoomAimedVerb(command, bundle, intentText, state);
};

/**
 * Is a question that names this room answerable (D137, D231)? By the room's
 * own `askNarratives` or `topics`; or, when the room is what is asked ABOUT,
 * by the topics of the thing addressed.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param roomId - The room the sentence is about
 * @returns Whether some answer exists for the roster to find
 */
const isRoomAskable = (command: ParsedCommand, bundle: EngineGameBundle, roomId: RoomId): boolean => {
	const room = bundle.indexes.roomsById.get(roomId);

	if (room?.askNarratives !== undefined || room?.topics !== undefined) {
		return true;
	}

	const addresseeRef = command.indirectObjectRef === undefined ? undefined : command.directObjectRef;

	if (addresseeRef === undefined || roomIdForEntityRef(addresseeRef) !== undefined) {
		return false;
	}

	// D232: an actor addressed about the room answers from its own table.
	const addresseeActorId = actorIdForEntityRef(addresseeRef);

	if (addresseeActorId !== undefined) {
		return bundle.indexes.actorsById.get(addresseeActorId)?.topics !== undefined;
	}

	return bundle.indexes.itemsById.get(itemIdForEntityRef(addresseeRef))?.topics !== undefined;
};

/**
 * D217: claim a command whose direct object is a SET, under the pack's `set`
 * policy (Mike's rulings, 2026-09-28) — the first interceptor, because a set
 * is nothing the others can read.
 *
 * @remarks
 * A set reaches narrowing only when the pack answers it in one line; the
 * walker has already split it into one command per member for `each`.
 * Looking is the one verb a set answers as a set: `examine open jars` reads
 * the group's line. Any other verb — `take open jars`, `seal open jars` — is
 * refused in one line, `pluralOneAtATime`, and nothing happens to the
 * things: a set answered in one line cannot also be acted on piecemeal
 * without saying which piece moved.
 *
 * @param command - The parser's proposal
 * @param bundle - The loaded bundle
 * @param intentText - The canonical verb
 * @param state - The live runtime state
 * @returns The set's look or the one-at-a-time refusal, or `undefined` when the object is not a set
 */
export const narrowSetCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome | undefined => {
	if (command.directObjectSetRefs === undefined) {
		return undefined;
	}

	return intentText === 'inspect'
		? narrowed({ itemIds: command.directObjectSetRefs.map((entityRef) => itemIdForEntityRef(entityRef)), type: 'inspect_set' })
		: rejected(bundle, state, { intentToken: intentText, type: 'plural_one_at_a_time' }, [
			speakSlot(bundle, state, 'pluralOneAtATime', { intent: intentText.toUpperCase() }),
		]);
};
